import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { useState, FormEvent } from 'react'
import { useLanguage } from '../contexts/LanguageContext'
import { useWaitlistMutation } from '../features/waitlist'
import SEOHead from '../components/SEOHead'

const Waitlist = () => {
  const { t, getLangPrefix } = useLanguage()
  const langPrefix = getLangPrefix()
  const [email, setEmail] = useState('')
  const [agreedToTerms, setAgreedToTerms] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState('')
  const [emailError, setEmailError] = useState('')
  const [termsError, setTermsError] = useState('')

  const mutation = useWaitlistMutation()

  const validateEmail = (emailValue: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(emailValue)
  }

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setEmail(value)
    if (value && !validateEmail(value)) {
      setEmailError(t.waitlist?.invalidEmail || 'Please enter a valid email address')
    } else {
      setEmailError('')
    }
  }

  const handleTermsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAgreedToTerms(e.target.checked)
    if (e.target.checked) {
      setTermsError('')
    }
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    
    let hasErrors = false
    
    if (!email || !validateEmail(email)) {
      setEmailError(t.waitlist?.invalidEmail || 'Please enter a valid email address')
      hasErrors = true
    } else {
      setEmailError('')
    }
    
    if (!agreedToTerms) {
      setTermsError('Please agree to the terms and conditions')
      hasErrors = true
    } else {
      setTermsError('')
    }

    if (hasErrors) {
      return
    }

    setError('')
    setEmailError('')
    setTermsError('')

    mutation.mutate(
      { email },
      {
        onSuccess: (data) => {
          if (data.success) {
            setSubmitted(true)
          } else {
            setError(data.error || t.waitlist?.errorSending || 'Error occurred while sending')
          }
        },
        onError: (err: Error) => {
          console.error('Error sending email:', err)
          setError(err.message || t.waitlist?.errorConnection || 'Failed to send email. Check server connection.')
        },
      }
    )
  }

  const loading = mutation.isPending
  const isEmailValid = email && validateEmail(email)
  const isFormValid = isEmailValid && agreedToTerms

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50/30 to-white relative overflow-hidden flex flex-col">
      <SEOHead />
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(16,185,129,0.12),transparent_60%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(5,150,105,0.08),transparent_60%)]" />
        <motion.div
          animate={{
            scale: [1, 1.4, 1],
            opacity: [0.12, 0.22, 0.12],
            rotate: [0, 120, 0],
            x: [0, 50, 0],
            y: [0, -30, 0],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
          className="absolute top-1/4 right-1/4 w-[600px] h-[600px] bg-gradient-to-br from-primary/18 via-primary-dark/12 to-primary-light/10 rounded-full blur-3xl"
        />
        <motion.div
          animate={{
            scale: [1.3, 1, 1.3],
            opacity: [0.08, 0.18, 0.08],
            rotate: [0, -60, 0],
            x: [0, -40, 0],
            y: [0, 40, 0],
          }}
          transition={{
            duration: 30,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
          className="absolute bottom-1/3 left-1/3 w-[500px] h-[500px] bg-gradient-to-tr from-primary-light/10 to-primary/6 rounded-full blur-3xl"
        />
        <div className="absolute inset-0 bg-[linear-gradient(135deg,transparent_0%,rgba(16,185,129,0.03)_25%,transparent_50%,rgba(5,150,105,0.02)_75%,transparent_100%)]" />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAzNGMwIDMuMzE0LTIuNjg2IDYtNiA2cy02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNiA2IDIuNjg2IDYgNnoiIGZpbGw9InJnYmEoMTYsMTg1LDEyOSwwLjAyKSIvPjwvZz48L3N2Zz4=')] opacity-40" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-2 md:py-6 lg:py-8 relative z-10 flex-1 flex flex-col overflow-y-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-2 md:mb-6 lg:mb-8"
        >
          <Link to={langPrefix} className="inline-flex items-center gap-2.5 group">
            <motion.div
              whileHover={{ scale: 1.1, rotate: 360 }}
              transition={{ duration: 0.6 }}
              className="w-10 h-10 md:w-12 md:h-12 relative"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-primary to-primary-dark rounded-full blur-md opacity-50 group-hover:opacity-75 transition-opacity" />
              <svg viewBox="0 0 32 32" fill="none" className="w-full h-full relative">
                <circle cx="16" cy="16" r="14" fill="url(#logo-gradient-waitlist)" />
                <path
                  d="M16 8C16 8 12 12 12 16C12 18.2091 13.7909 20 16 20C18.2091 20 20 18.2091 20 16C20 12 16 8 16 8Z"
                  fill="white"
                />
                <circle cx="16" cy="22" r="1.5" fill="white" />
                <defs>
                  <linearGradient
                    id="logo-gradient-waitlist"
                    x1="2"
                    y1="2"
                    x2="30"
                    y2="30"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#10b981" />
                    <stop offset="1" stopColor="#059669" />
                  </linearGradient>
                </defs>
              </svg>
            </motion.div>
            <span className="text-xl md:text-2xl font-black gradient-text group-hover:scale-105 transition-transform tracking-tight">
              Juvenis
            </span>
          </Link>
        </motion.div>

        <div className="max-w-4xl mx-auto text-center flex-1 flex flex-col justify-center px-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200, damping: 15 }}
            className="inline-flex items-center gap-2.5 bg-white/90 backdrop-blur-xl px-4 py-2 md:px-6 md:py-2.5 lg:px-7 lg:py-3 rounded-full border-2 border-primary/30 shadow-xl mb-4 md:mb-8 lg:mb-10 relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-primary-dark/5 to-primary/5 opacity-0 hover:opacity-100 transition-opacity duration-500" />
            <motion.div
              animate={{ scale: [1, 1.4, 1], opacity: [1, 0.5, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="w-3 h-3 bg-gradient-to-r from-primary via-primary-dark to-primary rounded-full relative z-10"
            />
                  <span className="text-xs md:text-sm lg:text-base font-black text-primary-dark tracking-widest uppercase relative z-10">
              {t.waitlist?.badge || 'Launching Soon'}
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, type: 'spring', stiffness: 80, damping: 12 }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-black mb-4 md:mb-6 lg:mb-8 text-gray-900 leading-[1.1] tracking-[-0.02em]"
          >
            <motion.span
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              className="block"
            >
              {t.waitlist?.title || 'Your path to'}
            </motion.span>
            <span className="relative inline-block mt-2 md:mt-3">
              <motion.span
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.5, type: 'spring' }}
                className="gradient-text bg-gradient-to-r from-primary via-primary-dark to-primary bg-clip-text text-transparent bg-[length:200%_auto] animate-gradient relative z-10"
              >
                {t.waitlist?.titleHighlight || 'longevity'}
              </motion.span>
              <motion.div
                initial={{ scaleX: 0, opacity: 0 }}
                animate={{ scaleX: 1, opacity: 1 }}
                transition={{ delay: 0.9, duration: 0.8, ease: 'easeOut' }}
                className="absolute -bottom-3 left-0 right-0 h-1.5 md:h-2 bg-gradient-to-r from-primary via-primary-dark to-primary rounded-full shadow-lg"
              />
              <motion.div
                initial={{ scaleX: 0, opacity: 0 }}
                animate={{ scaleX: 1, opacity: 1 }}
                transition={{ delay: 1.1, duration: 0.6, ease: 'easeOut' }}
                className="absolute -bottom-1 left-0 right-0 h-0.5 bg-gradient-to-r from-primary/40 via-primary-dark/40 to-primary/40 rounded-full blur-sm"
              />
            </span>
            <motion.span
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6 }}
              className="block mt-2 md:mt-3"
            >
              {t.waitlist?.titleEnd || 'starts here'}
            </motion.span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="text-base md:text-lg lg:text-xl xl:text-2xl text-gray-700 mb-6 md:mb-10 lg:mb-14 leading-relaxed max-w-3xl mx-auto font-medium"
          >
            {t.waitlist?.description || 'Be among the first to get access to'}{' '}
            <span className="font-black text-gray-900 relative">
              {t.waitlist?.descriptionHighlight || 'personal longevity plan'}
              <motion.span
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ delay: 1.2, duration: 0.6 }}
                className="absolute -bottom-1 left-0 right-0 h-0.5 bg-gradient-to-r from-primary/30 to-primary-dark/30"
              />
            </span>
            {t.waitlist?.descriptionEnd || ', based on scientific data and analysis of your biomarkers.'}
          </motion.p>

          {!submitted ? (
            <motion.form
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, type: 'spring', stiffness: 100, damping: 15 }}
              onSubmit={handleSubmit}
              className="space-y-5 md:space-y-6"
            >
              <div className="flex flex-col sm:flex-row gap-4 md:gap-5 lg:gap-6 max-w-3xl mx-auto">
                <motion.div
                  whileFocus={{ scale: 1.02 }}
                  className="flex-1 relative group"
                >
                  {emailError && (
                    <motion.p
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="absolute -top-8 left-0 text-sm text-red-600 z-20"
                    >
                      {emailError}
                    </motion.p>
                  )}
                  <motion.input
                    type="email"
                    value={email}
                    onChange={handleEmailChange}
                    placeholder={t.waitlist?.emailPlaceholder || 'your@email.com'}
                    required
                    whileFocus={{ scale: 1.02 }}
                    className={`w-full px-5 md:px-7 lg:px-9 py-4 md:py-5 lg:py-6 bg-white/95 backdrop-blur-sm border-[3px] rounded-2xl md:rounded-3xl focus:outline-none focus:ring-4 focus:ring-primary/30 text-base md:text-lg lg:text-xl font-semibold transition-all shadow-2xl hover:shadow-[0_20px_40px_rgba(0,0,0,0.1)] relative z-10 ${
                      emailError
                        ? 'border-red-400 focus:border-red-500 focus:ring-red-300'
                        : 'border-gray-300 focus:border-primary hover:border-primary/60'
                    }`}
                  />
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-primary/20 via-primary-dark/20 to-primary/20 rounded-2xl md:rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl -z-20" />
                </motion.div>
                <motion.button
                  whileHover={{ scale: loading || !isFormValid ? 1 : 1.08, y: loading || !isFormValid ? 0 : -4 }}
                  whileTap={{ scale: loading || !isFormValid ? 1 : 0.95 }}
                  type="submit"
                  disabled={loading || !isFormValid}
                  className="px-8 md:px-10 lg:px-14 py-4 md:py-5 lg:py-6 gradient-bg text-white rounded-2xl md:rounded-3xl font-black text-base md:text-lg lg:text-xl shadow-[0_20px_50px_rgba(16,185,129,0.3)] hover:shadow-[0_30px_70px_rgba(16,185,129,0.5)] transition-all premium-glow flex items-center justify-center gap-3 md:gap-3.5 lg:gap-4 group relative overflow-hidden min-w-[160px] md:min-w-[180px] lg:min-w-[220px] disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  <span className="relative z-10 font-black tracking-tight">
                    {loading ? '...' : (t.waitlist?.join || 'Join')}
                  </span>
                  <motion.svg
                    className="w-6 h-6 md:w-7 md:h-7 lg:w-8 lg:h-8 relative z-10"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    animate={{ x: [0, 6, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={3}
                      d="M13 7l5 5m0 0l-5 5m5-5H6"
                    />
                  </motion.svg>
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-primary-dark via-primary to-primary-dark bg-[length:200%_100%]"
                    initial={{ x: '-100%' }}
                    whileHover={{ x: '100%' }}
                    transition={{ duration: 0.6, ease: 'easeInOut' }}
                  />
                </motion.button>
              </div>
              {error && (
                <motion.p
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-sm text-red-600 max-w-2xl mx-auto mt-2 text-center"
                >
                  {error}
                </motion.p>
              )}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.9 }}
                className="max-w-3xl mx-auto px-2 sm:px-0"
              >
                <div className="flex justify-start items-center gap-2 sm:gap-3">
                  <input
                    type="checkbox"
                    id="terms-checkbox"
                    checked={agreedToTerms}
                    onChange={handleTermsChange}
                    className={`checkbox-white-check w-5 h-5 md:w-6 md:h-6 rounded border-2 focus:ring-2 focus:ring-primary/30 focus:ring-offset-0 cursor-pointer flex-shrink-0 mt-0.5 transition-colors ${
                      termsError
                        ? 'border-red-400 focus:border-red-500 focus:ring-red-300'
                        : 'border-gray-300'
                    }`}
                  />
                  <div className="flex">
                    <label
                      htmlFor="terms-checkbox"
                      className="text-xs sm:text-sm text-gray-700 cursor-pointer leading-relaxed"
                    >
                      {t.waitlist?.agreeToTerms || 'I agree to the'}{' '}
                      <Link
                        to={`${langPrefix}/privacy`}
                        className="text-primary hover:text-primary-dark underline font-semibold"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t.waitlist?.privacyPolicy || 'Privacy Policy'}
                      </Link>{' '}
                      {t.waitlist?.and || 'and'}{' '}
                      <Link
                        to={`${langPrefix}/terms`}
                        className="text-primary hover:text-primary-dark underline font-semibold"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t.waitlist?.termsOfUse || 'Terms of Use'}
                      </Link>
                    </label>
                  </div>
                </div>
                {termsError && (
                  <motion.p
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-xs sm:text-sm text-red-600 mt-2 ml-7 sm:ml-9"
                  >
                    {termsError}
                  </motion.p>
                )}
              </motion.div>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1 }}
                className="text-xs md:text-sm text-gray-500 max-w-2xl mx-auto mt-4 md:mt-5 flex items-center justify-center gap-2"
              >
                <span className="text-lg">🔒</span>
                <span>{t.waitlist?.privacy || 'We don\'t share your data with third parties. Only important updates about the launch.'}</span>
              </motion.p>
            </motion.form>
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.85, y: 50, rotateX: -10 }}
              animate={{ opacity: 1, scale: 1, y: 0, rotateX: 0 }}
              transition={{ type: 'spring', stiffness: 120, damping: 15 }}
              className="bg-white/98 backdrop-blur-xl rounded-2xl md:rounded-3xl lg:rounded-[2rem] p-8 md:p-12 lg:p-16 border-2 border-primary/30 shadow-[0_30px_80px_rgba(16,185,129,0.2)] premium-glow max-w-xl mx-auto relative overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-primary/8 via-transparent to-primary-dark/8" />
              <div className="absolute top-0 left-0 w-32 h-32 bg-gradient-to-br from-primary/10 to-transparent rounded-br-full blur-2xl" />
              <div className="absolute bottom-0 right-0 w-40 h-40 bg-gradient-to-tl from-primary-dark/10 to-transparent rounded-tl-full blur-2xl" />
              <div className="relative z-10">
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: 'spring', stiffness: 200, delay: 0.3 }}
                  className="w-20 h-20 md:w-24 md:h-24 lg:w-28 lg:h-28 gradient-bg rounded-2xl md:rounded-3xl flex items-center justify-center mx-auto mb-6 md:mb-8 shadow-[0_20px_60px_rgba(16,185,129,0.4)] relative"
                >
                  <motion.div
                    animate={{ rotate: [0, 360] }}
                    transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
                    className="absolute inset-0 rounded-2xl md:rounded-3xl border-4 border-primary/20"
                  />
                  <motion.svg
                    initial={{ pathLength: 0, scale: 0 }}
                    animate={{ pathLength: 1, scale: 1 }}
                    transition={{ duration: 0.8, delay: 0.5, type: 'spring' }}
                    className="w-10 h-10 md:w-12 md:h-12 lg:w-14 lg:h-14 text-white relative z-10"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={3.5}
                      d="M5 13l4 4L19 7"
                    />
                  </motion.svg>
                </motion.div>
                <motion.h2
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7, type: 'spring' }}
                  className="text-2xl md:text-3xl lg:text-4xl font-black mb-4 md:mb-5 text-gray-900 text-center"
                >
                  {t.waitlist?.success || 'You\'re on the list!'}{' '}
                  <motion.span
                    animate={{ rotate: [0, 10, -10, 10, 0] }}
                    transition={{ duration: 0.5, delay: 0.9 }}
                    className="inline-block"
                  >
                    🎉
                  </motion.span>
                </motion.h2>
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.8 }}
                  className="text-sm md:text-base lg:text-lg text-gray-600 leading-relaxed text-center"
                >
                  {t.waitlist?.successDescription || 'A personal manager will contact you shortly.'}
                </motion.p>
              </div>
            </motion.div>
          )}

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="mt-4 md:mt-8 lg:mt-12 pt-3 md:pt-5 lg:pt-6 border-t border-gray-200"
          >
            <Link
              to={langPrefix}
              className="text-xs text-gray-500 hover:text-primary transition-colors font-medium inline-block"
            >
              {t.waitlist?.back || '← Back to Home'}
            </Link>
          </motion.div>
        </div>
      </div>
    </div>
  )
}

export default Waitlist
